import { Button, Tooltip } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useOrderPiiReveal from '../../hooks/useOrderPiiReveal';

/**
 * 买家手机号「掩码 + 小眼睛」展示组件
 *
 * buyerPhone 在库中为 RSA 密文，列表/详情等常规接口返回的都是密文（约 344 字符），
 * 直接渲染会显示乱码。本组件默认以掩码展示，仅在用户点击小眼睛时调用
 * decrypt-detail 接口按单取明文（明文属敏感信息，不做默认展示）。
 *
 * 内部复用 `useOrderPiiReveal` Hook（与订单详情页、退货单 Drawer 的地址揭示同一份实现），
 * 但**只消费其 phone 部分**——本组件的语义仅限手机号。若页面需要同时揭示手机号与配送地址，
 * 请直接使用该 Hook 做区块级单一开关，而不要在此叠加地址渲染。
 *
 * 组件（经由 Hook）自持 reveal 状态，因此可直接用于表格行内 —— 每行独立揭示/收起，
 * 互不影响；翻页时组件卸载，状态自动重置。
 *
 * 调用方需自行判断原始值是否为空：为空时应直接渲染 '-'，不要挂载本组件，
 * 避免对没有手机号的订单也显示小眼睛并触发无意义的解密请求。
 *
 * @param {Object} props
 * @param {string} props.orderNo - 订单号，解密接口的唯一入参（仓库码后端从登录上下文取）
 */
const MASK = '••••••••';

const PhoneReveal = ({ orderNo }) => {
  const { t } = useTranslation();
  // PhoneReveal 只展示手机号，故仅请求解密 BUYER_PHONE，不请求 DELIVERY_ADDRESS
  const { revealed, loading, phone, toggle } = useOrderPiiReveal(orderNo, ['BUYER_PHONE']);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        style={{
          color: revealed ? undefined : '#8c8c8c',
          letterSpacing: revealed ? 'normal' : '2px',
        }}
      >
        {revealed ? phone || '-' : MASK}
      </span>
      <Tooltip
        title={
          revealed
            ? t('order.order.detail.delivery.hidePlaintext')
            : t('order.order.detail.delivery.viewPlaintext')
        }
      >
        <Button
          type="text"
          size="small"
          loading={loading}
          icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={toggle}
        />
      </Tooltip>
    </span>
  );
};

export default PhoneReveal;
